/**
 * Skills (manuales de moderación) — wireado contra /api/skills.
 *
 * `useSkillsForClient(clientSlug)` lista los skills del cliente.
 * `useSkill(skillId)` trae detalle + últimas 20 versiones.
 * `useCreateSkillVersion` agrega versión nueva.
 * `usePromoteVersion` cambia la versión productiva.
 * `useSkillDiff(skillId, vA, vB)` devuelve contenido de ambas versiones.
 * `useAppendValidatedCase` agrega caso validado desde el simulador.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import type { ClientSlug } from "@/types";

export interface SkillRow {
  id: string;
  clientSlug: ClientSlug;
  name: string;
  description: string | null;
  productionVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SkillVersionRow {
  id: string;
  skillId: string;
  versionNumber: number;
  content: string;
  source: string;
  meta: Record<string, unknown> | null;
  changeNote: string | null;
  createdBy: string;
  createdAt: string;
  promotedAt: string | null;
  promotedBy: string | null;
}

export interface SkillDetail {
  skill: SkillRow;
  versions: SkillVersionRow[];
  productionVersion: SkillVersionRow | null;
}

export function useSkillsForClient(clientSlug: ClientSlug | undefined) {
  return useQuery<SkillRow[]>({
    queryKey: ["skills", clientSlug],
    enabled: !!clientSlug,
    queryFn: async () => {
      if (!clientSlug) return [];
      const qs = new URLSearchParams({ clientSlug });
      const res = await apiGet<{ items: SkillRow[] }>(`/skills?${qs.toString()}`);
      return res.items;
    },
  });
}

export function useSkill(skillId: string | undefined) {
  return useQuery<SkillDetail>({
    queryKey: ["skill", skillId],
    enabled: !!skillId,
    queryFn: async () => apiGet<SkillDetail>(`/skills/${skillId}`),
  });
}

interface CreateSkillInput {
  clientSlug: ClientSlug;
  name: string;
  description?: string;
}

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation<SkillRow, Error, CreateSkillInput>({
    mutationFn: async (input) => {
      const res = await apiPost<{ ok: boolean; skill: SkillRow }>("/skills", input);
      return res.skill;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["skills"] });
      toast.success("Manual creado");
    },
    onError: (err) => toast.error(err.message),
  });
}

interface CreateVersionInput {
  skillId: string;
  content: string;
  notes?: string;
}

export function useCreateSkillVersion() {
  const qc = useQueryClient();
  return useMutation<SkillVersionRow, Error, CreateVersionInput>({
    mutationFn: async ({ skillId, content, notes }) => {
      const res = await apiPost<{ ok: boolean; version: SkillVersionRow }>(
        `/skills/${skillId}/versions`,
        { content, notes, sourceType: "manual_edit" },
      );
      return res.version;
    },
    onSuccess: (_v, vars) => {
      qc.invalidateQueries({ queryKey: ["skill", vars.skillId] });
      toast.success("Versión guardada");
    },
    onError: (err) => toast.error(err.message),
  });
}

export function usePromoteVersion() {
  const qc = useQueryClient();
  return useMutation<
    { skill: SkillRow; version: SkillVersionRow },
    Error,
    { skillId: string; versionId: string }
  >({
    mutationFn: async ({ skillId, versionId }) => {
      const res = await apiPost<{
        ok: boolean;
        skill: SkillRow;
        version: SkillVersionRow;
      }>(`/skills/${skillId}/promote`, { versionId });
      return { skill: res.skill, version: res.version };
    },
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ["skill", vars.skillId] });
      qc.invalidateQueries({ queryKey: ["skills"] });
      toast.success("Versión promovida a producción");
    },
    onError: (err) => toast.error(err.message),
  });
}

export interface SkillDiffResponse {
  vA: { id: string; versionNumber: number; content: string; createdAt: string };
  vB: { id: string; versionNumber: number; content: string; createdAt: string };
}

export function useSkillDiff(
  skillId: string | undefined,
  vA: string | undefined,
  vB: string | undefined,
) {
  return useQuery<SkillDiffResponse>({
    queryKey: ["skill-diff", skillId, vA, vB],
    enabled: !!(skillId && vA && vB),
    queryFn: async () => {
      const qs = new URLSearchParams({ vA: vA!, vB: vB! });
      return apiGet<SkillDiffResponse>(`/skills/${skillId}/diff?${qs.toString()}`);
    },
  });
}

interface AppendValidatedCaseInput {
  skillId: string;
  userMessage: string;
  rejectedReply: string;
  correctReply: string;
  notes?: string;
}

export function useAppendValidatedCase() {
  const qc = useQueryClient();
  return useMutation<SkillVersionRow, Error, AppendValidatedCaseInput>({
    mutationFn: async ({ skillId, ...body }) => {
      const res = await apiPost<{ ok: boolean; version: SkillVersionRow }>(
        `/skills/${skillId}/append-validated-case`,
        body,
      );
      return res.version;
    },
    onSuccess: (_v, vars) => {
      qc.invalidateQueries({ queryKey: ["skill", vars.skillId] });
      toast.success("Caso agregado al manual");
    },
    onError: (err) => toast.error(err.message),
  });
}
